import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useAetherContext } from '#pplx/frontend/aether/components/AetherProvider/useAetherContext.js';
import { getModalZIndex } from '#pplx/frontend/aether/components/internal/elevation/elevation.js';
import { useModalStackIndex } from '#pplx/frontend/aether/components/Modal/context/ModalStackContext.js';
import { ModalBackdrop } from '#pplx/frontend/aether/components/Modal/internal/ModalBackdrop.js';
import { ModalCloseButton } from '#pplx/frontend/aether/components/Modal/internal/ModalCloseButton.js';
import { ModalContent } from '#pplx/frontend/aether/components/Modal/internal/ModalContent.js';
import { BACKDROP_DEFAULT, getDefaultContentAriaProps, getModalChildrenState, getContentAriaProps, } from '#pplx/frontend/aether/components/Modal/internal/ModalInternals.js';
import { useActivationOrderedOverlayStacking } from '#pplx/frontend/overlay-stacking/useOverlayStacking.js';
import * as Dialog from '@radix-ui/react-dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { useTransition } from '@react-spring/web';
import { useCallback, useMemo } from 'react';
function getBackdropVariant({ isSpotlight, chromeless, backdrop, }) {
    if (isSpotlight)
        return 'transparent';
    if (chromeless && backdrop === 'frosted')
        return 'frosted';
    return BACKDROP_DEFAULT;
}
function getContentAnimation({ chromeless, animation, }) {
    return chromeless ? animation : 'scale';
}
export function DialogModal({ children, background = 'base', isOpen = true, onClose, dismissOnOutsideClick = true, onExitComplete, size = 'medium', variant = 'dialog', chromeless = false, shouldShowCloseButton = false, animation = 'scale', backdrop = BACKDROP_DEFAULT, height, width, maxWidth, ...restProps }) {
    const shouldUseActivationOrder = useActivationOrderedOverlayStacking();
    const { isMobileStyle } = useAetherContext();
    const isSpotlight = variant === 'spotlight';
    const modalStyle = useMemo(() => ({
        ...(height && { height }),
        ...(width && { width }),
        ...(maxWidth !== undefined && { maxWidth }),
    }), [height, width, maxWidth]);
    const stackIndex = useModalStackIndex();
    const zIndexStyle = useMemo(() => (shouldUseActivationOrder ? {} : { zIndex: getModalZIndex(stackIndex) }), [stackIndex, shouldUseActivationOrder]);
    const contentStyle = useMemo(() => ({
        ...zIndexStyle,
        ...modalStyle,
    }), [zIndexStyle, modalStyle]);
    const handleOpenChange = useCallback((open) => {
        if (!open) {
            onClose();
        }
    }, [onClose]);
    const { childArray, hasChildHeader, hasFooter, hasTitle } = useMemo(() => getModalChildrenState(children), [children]);
    const asBottomSheet = !!isMobileStyle && size !== 'fullscreen';
    // Chromeless modals render a VisuallyHidden title.
    // Standard modals rely on Modal.Header for the title and include a close button.
    const header = chromeless ? (_jsx(VisuallyHidden, { children: _jsx(Dialog.Title, {}) })) : null;
    const hasCloseButton = !chromeless || shouldShowCloseButton;
    const closeButton = hasCloseButton ? _jsx(ModalCloseButton, {}) : null;
    const layoutState = useMemo(() => ({
        isMobile: !!isMobileStyle,
        chromeless: !!chromeless,
        hasTitle,
        hasFooter,
        hasCloseButton,
    }), [isMobileStyle, chromeless, hasTitle, hasFooter, hasCloseButton]);
    // Modal.Header renders Dialog.Description internally when description is
    // provided, so Radix handles aria-describedby automatically. When there is
    // no description, suppress the auto-generated aria-describedby to avoid
    // pointing at a non-existent element.
    const contentAriaProps = useMemo(() => {
        if (chromeless) {
            return getContentAriaProps(true, restProps);
        }
        // Standard dialogs keep Modal.Header-driven labels and descriptions.
        return getDefaultContentAriaProps({ childArray, hasChildHeader });
    }, [chromeless, hasChildHeader, childArray, restProps]);
    const transitions = useTransition(isOpen, {
        from: { opacity: 0, scale: 0.8, x: 100, y: 100 },
        enter: { opacity: 1, scale: 1, x: 0, y: 0 },
        leave: { opacity: 0, scale: 0.8, x: 100, y: 100 },
        config: { tension: isMobileStyle ? 500 : 700, friction: 40, clamp: !!isMobileStyle },
        immediate: isSpotlight,
        onDestroyed: (open) => {
            if (open) {
                onExitComplete?.();
            }
        },
    });
    return (_jsx(Dialog.Root, { open: isOpen, onOpenChange: handleOpenChange, children: transitions((style, open) => open && (_jsxs(Dialog.Portal, { forceMount: true, children: [_jsx(Dialog.Overlay, { asChild: true, forceMount: true, children: _jsx(ModalBackdrop, { opacity: style.opacity, zIndexStyle: zIndexStyle, isVisible: true, variant: getBackdropVariant({ isSpotlight, chromeless, backdrop }) }) }), asBottomSheet ? (_jsxs(ModalContent, { variant: "bottom-sheet", background: background, y: style.y, contentStyle: contentStyle, contentAriaProps: contentAriaProps, dismissOnOutsideClick: dismissOnOutsideClick, layoutState: layoutState, children: [header, children, closeButton] })) : (_jsx("div", { className: "fixed inset-0 flex items-center justify-center pointer-events-none", style: zIndexStyle, children: _jsxs(ModalContent, { variant: "dialog", background: background, opacity: style.opacity, scale: style.scale, animation: getContentAnimation({ chromeless, animation }), size: size, contentStyle: contentStyle, contentAriaProps: contentAriaProps, dismissOnOutsideClick: dismissOnOutsideClick, layoutState: layoutState, children: [header, children, closeButton] }) }))] }))) }));
}
//# sourceMappingURL=DialogModal.js.map